import { useState, useEffect } from 'react';
import { toast } from 'sonner';

/**
 * Custom hook for persisting modal form data to localStorage
 * Automatically saves on every change, restores on open, clears on submit
 * 
 * @param {string} storageKey - Unique key for this modal (e.g., 'draft_addTeam')
 * @param {object} initialData - Initial/empty form state
 * @param {boolean} isOpen - Whether modal is open
 * @param {boolean} showToast - Show "Draft restored" toast when restoring
 */
const useModalPersistence = (storageKey, initialData, isOpen, showToast = true) => {
  const [formData, setFormData] = useState(initialData);
  const [hasDraft, setHasDraft] = useState(false);

  // Restore draft when modal opens
  useEffect(() => {
    if (isOpen) {
      const draft = localStorage.getItem(storageKey);
      if (draft) {
        try {
          const parsed = JSON.parse(draft);
          setFormData(parsed);
          setHasDraft(true);
          if (showToast) {
            toast.info('Draft restored', { duration: 2000 });
          }
        } catch (error) {
          console.error('Failed to parse draft:', error);
          localStorage.removeItem(storageKey);
        }
      }
    }
  }, [isOpen, storageKey, showToast]);

  // Save draft whenever form data changes
  useEffect(() => {
    if (isOpen && formData) {
      // Only save if form has actual data (not just initial empty state)
      const hasData = Object.values(formData).some(val => {
        if (Array.isArray(val)) return val.length > 0;
        if (typeof val === 'string') return val.trim() !== '';
        if (typeof val === 'number') return val !== 0;
        if (typeof val === 'boolean') return val !== false;
        return val != null;
      });
      
      if (hasData) {
        localStorage.setItem(storageKey, JSON.stringify(formData));
      }
    }
  }, [formData, isOpen, storageKey]);

  // Clear draft on successful submit
  const clearDraft = () => {
    localStorage.removeItem(storageKey);
    setFormData(initialData);
    setHasDraft(false);
  };

  // Discard draft and reset to initial
  const discardDraft = () => {
    localStorage.removeItem(storageKey);
    setFormData(initialData);
    setHasDraft(false);
    if (showToast) {
      toast.success('Draft discarded');
    }
  };

  return {
    formData,
    setFormData,
    hasDraft,
    clearDraft,
    discardDraft
  };
};

export default useModalPersistence;
